import { ShieldCheck, ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { C } from "../../theme/tokens";
import { PrimaryButton } from "../common/primitives";
import { RegLinkButton } from "../auth/RegLinkButton";
import { ProviderSwitch, useAccountIdField } from "../auth/AccountIdField";

/* Shown from LockedScreen once a trial account has used up its metered
   signal quota. Walks the user through creating a fresh Pocket Option
   account via our referral link — identical instructions to the
   SwitchAccountScreen shown during initial registration — then submits
   the new account's ID to renew/extend access.
   NOTE: submits via onSubmit (App.jsx -> useAuth.submitTrialRenewal),
   which currently posts to a placeholder backend endpoint that doesn't
   exist yet. */
export function TrialRenewalScreen({ t, wide, status, error, onSubmit, onBack }) {
  const idField = useAccountIdField();
  const { accountId } = idField;
  const submitting = status === "submitting";
  const success = status === "success";

  const steps = [t.reg_switch_step1, t.reg_switch_step2, t.reg_switch_step3, t.reg_switch_step4, t.reg_switch_step5];

  const handleSubmit = () => {
    const trimmed = accountId.trim();
    if (!trimmed || submitting) return;
    onSubmit(trimmed);
  };

  return (
    <div
      style={{
        minHeight: wide ? "60vh" : "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: wide ? 40 : 20,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(160deg, ${C.panel1} 0%, ${C.panel2} 140%)`,
            border: `1px solid ${C.hairline}`,
            padding: 22,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: C.deep,
              border: `1px solid ${C.green}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            {success ? <CheckCircle2 size={24} color={C.greenBright} /> : <ShieldCheck size={24} color={C.greenBright} />}
          </div>

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 21,
              color: C.primary,
              letterSpacing: "-0.01em",
            }}
          >
            {success ? t.trial_renew_success_title : t.reg_switch_title}
          </div>

          {success ? (
            <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
              {t.trial_renew_success_desc}
            </p>
          ) : (
            <>
              <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
                {t.reg_switch_desc1}
              </p>
              <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 12 }}>
                {t.reg_switch_desc2}
              </p>
              <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 12 }}>
                {t.reg_switch_desc3}
              </p>

              <div
                style={{
                  marginTop: 18,
                  borderRadius: 16,
                  background: C.panel1,
                  border: `1px solid ${C.hairline}`,
                  padding: "16px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                {steps.map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div
                      style={{
                        flex: "0 0 auto",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: C.deep,
                        border: `1px solid ${C.green}`,
                        color: C.greenBright,
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: 1,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ color: C.primary, fontSize: 13.5, lineHeight: 1.5 }}>{step}</div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 16,
                  color: C.greenBright,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 13.5,
                }}
              >
                {t.reg_switch_note}
              </div>

              <RegLinkButton t={t} label={t.reg_switch_link} />

              <div style={{ marginTop: 24 }}>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 15,
                    color: C.primary,
                  }}
                >
                  {t.reg_id_label}
                </div>
                <ProviderSwitch
                  provider={idField.provider}
                  onChange={idField.setProvider}
                  t={t}
                  disabled={submitting}
                />
                <div style={{ fontSize: 12.5, color: C.mutedB, marginTop: 4, marginBottom: 12 }}>
                  {idField.hint(t)}
                </div>

                <input
                  id="pj-trial-renew-account-id"
                  {...idField.inputProps(t)}
                  style={{
                    width: "100%",
                    background: C.panel1,
                    border: `1px solid ${error ? "#C4604A" : C.green}`,
                    borderRadius: 14,
                    color: C.primary,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: 15,
                    textAlign: "center",
                    padding: "16px 14px",
                    outline: "none",
                  }}
                />

                {error && (
                  <div style={{ color: "#C4604A", fontSize: 12.5, marginTop: 8, textAlign: "center" }}>
                    {t.trial_renew_error}
                  </div>
                )}

                <div style={{ marginTop: 12 }}>
                  {/*
                  <PrimaryButton onClick={handleSubmit} disabled={!accountId.trim() || submitting}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {submitting ? <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} /> : null}
                      {t.reg_verify} {!submitting && <ArrowRight size={16} />}
                    </span>
                  </PrimaryButton>
                  */}
                </div>
              </div>
            </>
          )}

          {/*
          <button
            onClick={onBack}
            style={{
              marginTop: 10,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "15px 18px",
              borderRadius: 14,
              border: `1px solid ${C.hairline}`,
              background: "transparent",
              color: C.primary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 14.5,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={16} />
            {t.reg_switch_back}
          </button>
          */}
        </div>
      </div>
    </div>
  );
}
